import { useCallback } from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import api from 'api';
import {
  NotificationGroup,
  NotificationsStatus,
  NotificationType,
  NOTIFICATIONS_LIMIT,
} from 'models/Notification';
import { PaginatedData } from 'models/PaginatedData';
import { getNextPageParam } from 'queryUtils';
import { getIsNotificationUnread } from 'utils';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

const NOTIFICATIONS_QUERY_KEY = 'notifications';

type UseNotificationsQueryParams = {
  group: NotificationGroup;
  status: NotificationsStatus;
  enabled?: boolean;
};

export default function useNotificationsQuery({
  group,
  status,
  enabled = true,
}: UseNotificationsQueryParams) {
  const queryClient = useQueryClient();

  const getMixpanelProps = useGetMixpanelProps();

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] }),
    [queryClient],
  );

  // Helper to update notification items across all cached pages
  const updateNotifications = useCallback(
    (updater: (items: NotificationType[]) => NotificationType[]) => {
      queryClient.setQueriesData<InfiniteData<PaginatedData<NotificationType>>>(
        { queryKey: [NOTIFICATIONS_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: updater(page.items),
            })),
          };
        },
      );
    },
    [queryClient],
  );

  const query = useInfiniteQuery<PaginatedData<NotificationType>>({
    queryKey: [NOTIFICATIONS_QUERY_KEY, { group, status }],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await api.notifications.getNotifications({
        $page: pageParam.toString(),
        $limit: `${NOTIFICATIONS_LIMIT}`,
        $sort: '-createdAt',
        $relations:
          '["sender", "listing", "dataRoomQuestion", "dealRoom", "lead", "firstParticipant", "secondParticipant", "shareableLink"]',
        group,
        ...(getIsNotificationUnread(status) && { isRead: false }),
      });

      return data;
    },
    enabled,
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    getNextPageParam,
  });

  return { query, invalidate, updateNotifications };
}
